<template>
  <tr>
    <th class="w-8"></th>
    <th class="5xl:pl-7! min-w-96 pr-0! pl-2!">
      <div class="5xl:gap-7 flex items-center justify-start gap-2">
        <RfFilter
          hideIcon
          tableStyle
          activator="Status"
          explicit
          :groups="{ statuses: { el: StatusesConstants, key: 'type', value: 'title' } }"
          :selected="filters"
          @selected="items => emit('update:filters', { ...filters, ...items })"
        />
        <RfFilter
          hideIcon
          tableStyle
          class="rf-header--device [&.rf-menu]:justify-start [&.rf-menu_.rf-menu--button]:relative [&.rf-menu_.rf-menu--button]:left-3 [&.rf-menu_.rf-menu--button]:ml-28"
          activator="Device / type / creator"
          explicit
          :groups="{
            devices: {
              el: getAppDevices,
              key: 'value',
              value: 'text',
              name: 'Device Platform',
            },
            prompts: { el: getAllPromptTypes(), key: 'key', value: 'value', name: 'Prompt type' },
            creator_ids: {
              el: users,
              key: 'id',
              value: getUserName,
              name: 'Creator',
            },
          }"
          :selected="filters"
          @selected="items => emit('update:filters', { ...filters, ...items })"
        />
      </div>
    </th>

    <th class="text-left">
      <span class="flex">
        <RfFilter
          hideIcon
          tableStyle
          class="grow-0"
          explicit
          activator="Tags"
          :groups="{ tags: { el: tags, key: 'id', value: 'title' } }"
          :selected="filters"
          @selected="items => emit('update:filters', { ...filters, ...items })"
        >
          <template v-if="appId" #prepend>
            <RouterLink
              :to="`/apps/${appId}/settings/tags`"
              class="text-buttons flex w-full items-center justify-between text-blue-50"
            >
              Open in settings <RfLinkIcon class="h-6! w-6! stroke-blue-50!" />
            </RouterLink>
            <hr v-if="tags?.length" class="border-strokes h-0 w-full border-t" />
          </template>
          <template #item="{ el }">
            <RfColorTag
              class="p-0! *:py-1!"
              :backgroundColor="el.color"
              :title="el.title"
              pill
              disabled
            />
          </template>
        </RfFilter>
      </span>
    </th>

    <th v-if="engagementCategoriesEnabled" class="text-left">
      <span class="flex">
        <RfFilter
          hideIcon
          tableStyle
          class="grow-0"
          explicit
          activator="Engagement Category"
          :groups="{
            engagement_category: {
              el: engagementCategories,
              key: 'value',
              value: 'label',
            },
          }"
          :selected="filters"
          @selected="items => emit('update:filters', { ...filters, ...items })"
        >
          <template #item="{ el }">
            <RfColorTag
              v-if="el.key"
              :backgroundColor="getCategoryColor(el.key)"
              :title="getCategoryLabel(el.key)"
              pill
              disabled
              class="p-0! *:py-1!"
            />
            <span v-else class="text-body text-beige-50">None</span>
          </template>
        </RfFilter>
      </span>
    </th>

    <template v-if="!performanceCols">
      <th class="5xl:min-w-48 w-24 text-left">
        <span class="flex">
          <RfFilter
            hideIcon
            tableStyle
            class="grow-0"
            explicit
            activator="Segments"
            :groups="{ segments: { el: segments, key: 'id', value: 'name' } }"
            :selected="filters"
            @selected="items => emit('update:filters', { ...filters, ...items })"
          />
        </span>
      </th>
      <th class="5xl:min-w-32 min-w-24.5">
        <RfSortButton
          :filtered="sort.type === 'start_date' ? sort.value : null"
          class="h-full w-full justify-start"
          @click="value => emit('update:sort', { type: 'start_date', value })"
        >
          Start
        </RfSortButton>
      </th>
      <th class="5xl:min-w-32 min-w-24.5">
        <RfSortButton
          :filtered="sort.type === 'end_date' ? sort.value : null"
          class="h-full w-full justify-start"
          @click="value => emit('update:sort', { type: 'end_date', value })"
        >
          Finish
        </RfSortButton>
      </th>
    </template>

    <th class="bg-goal min-w-24">
      <RfSortButton
        :filtered="sort.type === 'users' ? sort.value : null"
        class="h-full w-full"
        disabled
        @click="value => emit('update:sort', { type: 'users', value })"
      >
        Users
      </RfSortButton>
    </th>

    <template v-if="performanceCols">
      <th class="bg-goal min-w-24">
        <RfSortButton
          :filtered="sort.type === 'users' ? sort.value : null"
          class="h-full w-full"
          disabled
          @click="value => emit('update:sort', { type: 'impressions', value })"
        >
          Impressions
        </RfSortButton>
      </th>
      <th class="bg-goal min-w-24">
        <RfSortButton
          :filtered="sort.type === 'users' ? sort.value : null"
          class="h-full w-full"
          disabled
          @click="value => emit('update:sort', { type: 'click', value })"
        >
          Clicks
        </RfSortButton>
      </th>
      <th class="bg-goal min-w-24">
        <RfSortButton
          :filtered="sort.type === 'users' ? sort.value : null"
          class="h-full w-full"
          disabled
          @click="value => emit('update:sort', { type: 'ctr', value })"
        >
          CTR
        </RfSortButton>
      </th>
    </template>

    <th class="bg-goal min-w-24">
      <RfSortButton
        :filtered="sort.type === 'goal' ? sort.value : null"
        disabled
        @click="value => emit('update:sort', { type: 'goal', value })"
      >
        Goal
      </RfSortButton>
    </th>
    <th class="bg-goal min-w-24">
      <RfSortButton
        :filtered="sort.type === 'conversion' ? sort.value : null"
        disabled
        @click="value => emit('update:sort', { type: 'conversion', value })"
      >
        Conv.
      </RfSortButton>
    </th>
    <th class="min-w-10"></th>
  </tr>
</template>

<script setup>
import StatusesConstants from "@/utils/constants/PromptStatusesConstants";
import { getAllPromptTypes } from "@/utils/getDeviceName";
import RfSortButton from "@/components/buttons/RfSortButton.vue";
import RfFilter from "@/components/inputs/RfFilter.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { getCategoryColor, getCategoryLabel } from "@/utils/constants/EngagementCategoryConstants";
import RfLinkIcon from "@/components/icons/RfLinkIcon.vue";
import { getUserName } from "@/utils/stringHelpers";
import { RouterLink } from "vue-router";

const emit = defineEmits(["update:filters", "update:sort"]);

defineProps({
  filters: { type: Object, default: null },
  sort: { type: Object, default: null },
  getAppDevices: { type: Array, default: () => [] },
  segments: { type: Array, default: () => [] },
  tags: { type: Array, default: () => [] },
  users: { type: Array, default: () => [] },
  performanceCols: { type: Boolean, default: false },
  appId: { type: String, default: "" },
  engagementCategories: { type: Array, default: () => [] },
  engagementCategoriesEnabled: { type: Boolean, default: false },
});
</script>
